import { cmsGraphQLClient } from '@app/graphQLClient';
import { useParsePaginationFromUrl } from '@app/hooks/useParsePaginationFromUrl';
import RectangleAd from '@components/Advertisements/RectangleAd';
import { BoatsCardsList } from '@components/BoatsCardsList';
import Jumbotron from '@components/Jumbotron';
import { Layout } from '@components/Layout';
import { dehydrate } from '@tanstack/react-query';
import { Breadcrumb } from 'antd';
import { DEFAULT_PER_PAGE, TWO_MINUTES_IN_MILLISECONDS } from 'mdc-constants';
import { GetStaticProps } from 'next';
import Link from 'next/link';
import React, { useMemo } from 'react';
import {
  BoatsType,
  GetBoatsTypesBySlugVariables,
  GetTypeBoatsVariables,
  useGetAllBoatsTypesId,
  useGetBoatsTypesBySlug,
  useGetTypeBoats,
} from 'services/graphql/cms';
import { useSubscription } from 'services/hooks/queries/useSubscription';
import { useTypeBoats } from 'services/hooks/queries/useTypeBoats';
import { queryClient } from 'services/queryClient';

interface PageProps {
  boatsType: BoatsType;
}

const SingleTypePage: React.FC<PageProps> = ({ boatsType }) => {
  const metaData = {
    title: boatsType.name,
  };

  const { isSubscribed } = useSubscription();

  const { currentPage, currentPerPage, orderBy } = useParsePaginationFromUrl();

  const sort = useMemo<string[]>(() => {
    if (orderBy === 'MOST_VIEWED') {
      return ['views_count:desc'];
    }

    if (orderBy === 'A_TO_Z') {
      return ['year_first:desc', 'model:desc'];
    }

    return ['createdAt:desc'];
  }, [orderBy]);

  const publicBoatsVariables: GetTypeBoatsVariables = {
    typeId: boatsType.documentId,
    page: currentPage,
    pageSize: currentPerPage,
    sort,
  };

  const { data, isFetching, isPlaceholderData } = useTypeBoats(publicBoatsVariables);
  const isSpinning = useMemo(() => isFetching && isPlaceholderData, [isFetching, isPlaceholderData]);

  return (
    <Layout meta={metaData}>
      <main>
        <Jumbotron
          breadCrumb={[0]}
          pageName="explore_type"
          title={boatsType.name}
          desc={boatsType.description}
          breadCrumbChildren={
            <>
              <Breadcrumb.Item>
                <Link href="/boats/type">Types</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{boatsType.name}</Breadcrumb.Item>
            </>
          }
        />

        {!isSubscribed ? (
          <div style={{ padding: '0 24px' }}>
            <RectangleAd />
          </div>
        ) : null}
        <BoatsCardsList boats={data?.boats_connection} isSpinning={isSpinning} />
      </main>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug: string = context.params.slug as string;

  const variables: GetBoatsTypesBySlugVariables = {
    slug,
  };

  const fetchedBoatsTypes = await useGetBoatsTypesBySlug.fetcher(cmsGraphQLClient, variables)();

  if (fetchedBoatsTypes.boatsTypes.length) {
    const [boatsType] = fetchedBoatsTypes.boatsTypes;

    const publicBoatsVariables: GetTypeBoatsVariables = {
      typeId: boatsType.documentId,
      page: 1,
      pageSize: DEFAULT_PER_PAGE,
      sort: ['createdAt:desc'],
    };

    await queryClient.prefetchQuery({
      queryKey: ['getTypeBoats', publicBoatsVariables],
      queryFn: useGetTypeBoats.fetcher(cmsGraphQLClient, publicBoatsVariables),
      staleTime: TWO_MINUTES_IN_MILLISECONDS,
      gcTime: TWO_MINUTES_IN_MILLISECONDS,
    });

    return {
      props: {
        boatsType,
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  return {
    notFound: true,
  };
};

export const getStaticPaths = async () => {
  const boatsTypesData = await useGetAllBoatsTypesId.fetcher(cmsGraphQLClient)();

  if (boatsTypesData.boatsTypes.length) {
    const paths = boatsTypesData.boatsTypes.map((item) => ({
      params: { slug: item.slug },
    }));

    return {
      paths,
      fallback: false,
    };
  }

  return {
    paths: [],
    fallback: true,
  };
};
export default SingleTypePage;
